import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function IncidentIoIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Incident.io" },
  ];

  return (
    <DocLayout
      title="Incident.io Integration - Uptime Monitor Documentation"
      description="Connect Uptime Monitor with Incident.io for modern incident response with automated workflows and Slack-native collaboration."
      canonical="https://uptime-monitor.io/docs/notifications/incident-io"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Incident.io Integration"
        description="Send monitor alerts to incident.io using the HTTP Alert Events API. Automatically create and resolve alerts based on monitor status."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>How It Works</h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor integrates with incident.io through the HTTP Alert Events API:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Sends &quot;firing&quot; events when monitors go down
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Sends &quot;resolved&quot; events when monitors recover (configurable)
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Includes monitor details and error information in metadata
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Uses deduplication keys to prevent duplicate alerts
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Links to monitor dashboard for quick access
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Setup Guide */}
      <DocSection title="Setup Instructions">
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 1: Get Alert Source URL in incident.io
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Log in to your incident.io dashboard
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Navigate to{" "}
                      <strong className={docsColors.text.heading}>Settings → Alert Sources</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Create alert source</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Select <strong className={docsColors.text.heading}>HTTP</strong> as the source
                      type
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Name it &quot;Uptime Monitor&quot; and save
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Copy the <strong className={docsColors.text.heading}>Webhook URL</strong> and{" "}
                      <strong className={docsColors.text.heading}>Bearer token</strong>
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>

        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 2: Configure in Uptime Monitor
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In Uptime Monitor, go to{" "}
                      <strong className={docsColors.text.heading}>Settings → Contacts</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Add Contact</strong> and
                      select <strong className={docsColors.text.heading}>incident.io</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Enter a name (e.g., &quot;Production Alerts&quot;)
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Paste your webhook URL and bearer token
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Configure auto-resolve setting (enable to automatically resolve alerts when
                      monitors recover)
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Save the contact and add it to your monitors
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>
      </DocSection>

      {/* Event Format */}
      <DocSection title="Alert Event Format">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor sends the following event structure to incident.io:
          </p>

          <DocCodeBlock className="overflow-x-auto">
            <pre className={`text-sm ${docsColors.text.body}`}>
              <code>{`{
  "title": "Production API is DOWN",
  "description": "Monitor is not responding. Reason: Connection timeout after 30000ms",
  "deduplication_key": "507f1f77bcf86cd799439011-507f1f77bcf86cd799439012",
  "status": "firing",
  "metadata": {
    "service": "uptime-monitor",
    "monitor_id": "507f1f77bcf86cd799439011",
    "monitor_name": "Production API",
    "monitor_url": "HEAD https://api.example.com/",
    "monitor_type": "HTTPS",
    "monitor_dashboard": "https://uptime-monitor.io/dashboard/monitors/507f1f77bcf86cd799439011",
    "incident_id": "507f1f77bcf86cd799439012",
    "check_interval": 60,
    "timeout": 30
  }
}`}</code>
            </pre>
          </DocCodeBlock>

          <div className="mt-4 space-y-3">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Event Status</h3>
              <ul className={`space-y-1 text-sm ${docsColors.text.body}`}>
                <li>
                  • <strong className={docsColors.text.heading}>firing:</strong> Monitor is down
                </li>
                <li>
                  • <strong className={docsColors.text.heading}>resolved:</strong> Monitor has
                  recovered
                </li>
              </ul>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Deduplication</h3>
              <p className={`text-sm ${docsColors.text.body}`}>
                Uses format
                <code className="px-2 py-1 rounded text-green-800 dark:text-green-400">
                  {"{monitor_id}"}-{"{incident_id}"}
                </code>
                to ensure each incident creates only one alert in incident.io.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Enable auto-resolve for cleaner alert tracking
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Automatically resolve alerts when monitors recover
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Create separate alert sources for different environments
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use different webhook URLs for production vs staging
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use meaningful monitor names</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Monitor names appear in alert titles - make them descriptive
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Test integration before production use
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Use the Test button in contact settings to verify alerts are created correctly in
                  incident.io
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <DocAlert variant="warning" className="p-6">
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>Common Issues</h3>
          <div className={`space-y-4 ${docsColors.text.body}`}>
            <div>
              <strong className={docsColors.text.heading}>Alerts not appearing:</strong>
              <ul className="mt-1 ml-4 text-sm space-y-1">
                <li>• Verify webhook URL and bearer token are correct</li>
                <li>• Ensure monitor has incident.io contact assigned</li>
                <li>• Check monitor is not paused</li>
                <li>• Test with a different monitor to isolate issues</li>
              </ul>
            </div>
            <div>
              <strong className={docsColors.text.heading}>Authentication errors:</strong>
              <ul className="mt-1 ml-4 text-sm space-y-1">
                <li>• Verify bearer token is correctly pasted (no spaces or extra characters)</li>
                <li>• Check webhook URL matches the one from incident.io</li>
                <li>• Regenerate the alert source in incident.io if needed</li>
              </ul>
            </div>
          </div>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/pagerduty", text: "PagerDuty" }}
        next={{ href: "/docs/notifications/opsgenie", text: "Opsgenie" }}
      />
    </DocLayout>
  );
}
